import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useSession, useSupabaseClient } from '@supabase/auth-helpers-react';
import {
  WorkflowItem,
  WorkflowStatus,
  WorkflowType,
  PATH_TO_TYPE,
  SEARCH_DEBOUNCE_DELAY,
  RWC_REGION_OPTIONS,
  shortStatusLabel,
  groupItemsByStatus,
} from './config';
import { useUrlFilters, WORKFLOW_FILTER_DEFINITIONS } from '../../utils/useUrlFilters';
import KanbanBoard from './components/KanbanBoard';
import MoveToModal from './components/MoveToModal';
import { useCollapsedColumns, useColumnScrollSpy } from './useBoardNavigation';
import Dropdown from '../../components/Dropdown';
import SearchableClientDropdown from '../../components/SearchableClientDropdown';
import { DropdownItem } from '../../components/Dropdown/config';
import { useUserRole } from '../../utils/useUserRole';
import ProjectDetailModal from '../../components/ProjectDetailModal';
import { ProjectDetailTab } from '../../components/ProjectDetailModal/config';
import { useProjectDetailReopen } from '../../components/ProjectDetailModal/useProjectDetailReopen';
import AddJobModal from '../../components/AddJobModal';
import AddStageModal from '../../components/AddStageModal';
import { PlusIcon } from '../../components/IconButton/icons';
import { errorMessage, notifyReadOnly, notifySaveFailure } from '../../utils/supabaseErrors';
import {
  canUseManagerOnlyStatuses,
  isManagerOnlyWorkflowStatus,
  managerOnlyStatusMessage,
} from '../../utils/workflowStatusAccess';
import { useConfirmDialog } from '../../components/ConfirmDialog';
import ListPage, { FilterTrigger, SearchInput, StatusSegments } from '../../components/ListPage';
// Note: styles.scss is imported in main.scss

const WORKFLOW_TITLES: Record<WorkflowType, string> = {
  Design: 'Design Workflow',
  Advisory: 'Advisory Workflow',
  RWC: 'RWC Workflow',
  RWD: 'RWD Workflow',
};

// =======================================
// INTERFACES
// =======================================

interface TeamMember {
  id: string;
  first_name: string;
  last_name: string;
  type: string;
}

interface AssignableTeamMember {
  id: string;
  first_name: string;
  last_name: string;
  full_name: string;
  type: string;
}

// =======================================
// CUSTOM HOOKS
// =======================================

/**
 * Debounce hook for search input
 */
const useDebounce = <T,>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// =======================================
// MAIN COMPONENT
// =======================================

const Workflows: React.FC = () => {
  const { workflowType: workflowTypePath } = useParams<{ workflowType: string }>();
  const session = useSession();
  const supabase = useSupabaseClient();
  const userRole = useUserRole();
  const { isReadOnly } = userRole;
  const canUseManagerOnly = canUseManagerOnlyStatuses(userRole);
  const { notify, dialog: noticeDialog } = useConfirmDialog();

  // Determine workflow type from URL
  const workflowType: WorkflowType = useMemo(() => {
    return PATH_TO_TYPE[workflowTypePath?.toLowerCase() || ''] || 'Design';
  }, [workflowTypePath]);

  // Data state
  const [statuses, setStatuses] = useState<WorkflowStatus[]>([]);
  const [items, setItems] = useState<WorkflowItem[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [assignableTeamMembers, setAssignableTeamMembers] = useState<AssignableTeamMember[]>([]);
  // Track current user's team member ID for default filter initialization
  const [, setCurrentUserTeamMemberId] = useState<string | null>(null);

  // Loading and error state
  const [initialLoading, setInitialLoading] = useState(true);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // URL-synced filter state - persists across navigation
  const { 
    filters: urlFilters, 
    setFilter,
    setFilters: setUrlFilters
  } = useUrlFilters(WORKFLOW_FILTER_DEFINITIONS);

  // Derive filter values from URL state
  const searchInput = urlFilters.search;
  const selectedClient = useMemo(() => 
    urlFilters.client ? [urlFilters.client] : [], 
    [urlFilters.client]
  );
  const selectedRegion = urlFilters.region;
  
  // Track if the default team member filter has been initialized from URL or user preference
  const [defaultTeamMemberInitialized, setDefaultTeamMemberInitialized] = useState(false);
  
  // selectedTeamMember: null = not yet initialized, array = ready to use
  // We need to handle the case where URL has team member filter vs. default to current user
  const [selectedTeamMember, setSelectedTeamMember] = useState<string[] | null>(null);

  // Track if initial load is complete (to distinguish from filter changes)
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Modal state
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [selectedStageId, setSelectedStageId] = useState<number | null>(null);
  const [detailTab, setDetailTab] = useState<ProjectDetailTab | undefined>(undefined);
  const [selectedStageInfo, setSelectedStageInfo] = useState<{
    jobNumber: string;
    stageDescription: string;
    parentDescription: string;
  } | null>(null);
  const [isAddJobModalOpen, setIsAddJobModalOpen] = useState(false);
  const [isAddStageModalOpen, setIsAddStageModalOpen] = useState(false);
  const [moveTarget, setMoveTarget] = useState<WorkflowItem | null>(null);

  // Board navigation: collapsed columns and the column rail's scroll-spy
  const boardRef = useRef<HTMLDivElement>(null);

  // Debounced search term
  const debouncedSearch = useDebounce(searchInput, SEARCH_DEBOUNCE_DELAY);

  // =======================================
  // DATA FETCHING
  // =======================================

  /**
   * Fetch workflow statuses for the current type
   */
  const fetchStatuses = useCallback(async (): Promise<WorkflowStatus[]> => {
    const { data, error: rpcError } = await supabase
      .rpc('get_workflow_statuses_for_type', { p_job_type: workflowType });

    if (rpcError) throw rpcError;
    return data || [];
  }, [supabase, workflowType]);

  /**
   * Fetch workflow items with current filters
   */
  const fetchItems = useCallback(async (
    searchTerm?: string,
    projectLeadFilter?: string[],
    clientFilter?: string[],
    teamMemberFilter?: string[],
    regionFilter?: string[]
  ): Promise<WorkflowItem[]> => {
    const { data, error: rpcError } = await supabase
      .rpc('get_workflow_items', {
        p_job_type: workflowType,
        p_search_term: searchTerm || null,
        p_project_lead_filter: projectLeadFilter && projectLeadFilter.length > 0 ? projectLeadFilter : null,
        p_client_filter: clientFilter && clientFilter.length > 0 ? clientFilter : null,
        p_team_member_filter: teamMemberFilter && teamMemberFilter.length > 0 ? teamMemberFilter : null,
        p_region_filter: regionFilter && regionFilter.length > 0 ? regionFilter : null
      });

    if (rpcError) throw rpcError;
    return data?.items || [];
  }, [supabase, workflowType]);

  /**
   * Fetch team members for project lead filter dropdown (Engineers only)
   */
  const fetchTeamMembers = useCallback(async (): Promise<TeamMember[]> => {
    const { data, error: fetchError } = await supabase
      .from('active_team_members')
      .select('id, first_name, last_name, type')
      .eq('type', 'Engineer')
      .order('first_name');

    if (fetchError) throw fetchError;
    return data || [];
  }, [supabase]);

  /**
   * Fetch assignable team members for team member filter (Engineers and Draftspersons)
   */
  const fetchAssignableTeamMembers = useCallback(async (): Promise<AssignableTeamMember[]> => {
    const { data, error: fetchError } = await supabase
      .rpc('search_assignable_team_members', { search_term: null, limit_results: 100 });

    if (fetchError) throw fetchError;
    return data || [];
  }, [supabase]);

  /**
   * Fetch current user's team_member_id from profiles
   */
  const fetchCurrentUserTeamMemberId = useCallback(async (): Promise<string | null> => {
    if (!session?.user?.id) return null;
    
    const { data, error: fetchError } = await supabase
      .from('profiles')
      .select('team_member_id')
      .eq('id', session.user.id)
      .single();

    if (fetchError) {
      console.error('Error fetching user profile:', fetchError);
      return null;
    }
    return data?.team_member_id || null;
  }, [supabase, session?.user?.id]);

  /**
   * Refetch items only (for filter changes)
   */
  const refetchItems = useCallback(async () => {
    if (!initialLoadComplete) return;
    if (selectedTeamMember === null) return; // Wait for initialization
    
    setItemsLoading(true);
    try {
      const newItems = await fetchItems(debouncedSearch, [], selectedClient, selectedTeamMember, selectedRegion);
      setItems(newItems);
    } catch (err) {
      console.error('Error fetching workflow items:', err);
      setError('Failed to load workflow items');
    } finally {
      setItemsLoading(false);
    }
  }, [initialLoadComplete, fetchItems, debouncedSearch, selectedClient, selectedTeamMember, selectedRegion]);

  // Memoize valid status IDs for quick lookup (avoid recreating Set on every drag)
  const validStatusIds = useMemo(() => new Set(statuses.map(s => s.id)), [statuses]);

  // Items whose status isn't a board column are off-board entirely. 'Unsuccessful'
  // is deliberately absent from workflow_status_types so it never becomes a
  // column; without this filter those items would fall through to the
  // first-column fallback below and a lost job would reappear at the top of the
  // pipeline. Items with NO status still legitimately fall through to column 1.
  // Skipped while statuses are still loading, so the board isn't briefly empty.
  const boardItems = useMemo(
    () =>
      statuses.length === 0
        ? items
        : items.filter(
            (item) => !item.workflow_status_id || validStatusIds.has(item.workflow_status_id),
          ),
    [items, statuses.length, validStatusIds],
  );

  /**
   * Move a card to another column (a drop, or the Move to sheet). The card
   * lands in the column's fixed sort position, so only its status changes.
   */
  const handleStatusChange = useCallback(async (
    itemId: number,
    entityType: 'project' | 'stage',
    newStatusId: number
  ) => {
    const newStatus = statuses.find(s => s.id === newStatusId);

    // A drop on Complete or Archived by a non-manager is stopped here, before
    // the card moves: a drop target cannot be greyed out mid-drag the way a
    // menu row can, so the refusal has to be said. The trigger is the real
    // gate (see enforce_manager_only_workflow_status); while the role is still
    // loading the move goes through and the database decides.
    if (newStatus && !canUseManagerOnly && isManagerOnlyWorkflowStatus(workflowType, newStatus.status_name)) {
      notify({
        title: 'Managers only',
        message: `${managerOnlyStatusMessage(newStatus.status_name)} Ask a manager to move it for you.`,
        tone: 'info',
      });
      return;
    }

    // Optimistic update
    setItems(prevItems => prevItems.map(item =>
      item.id === itemId && item.entity_type === entityType
        ? {
            ...item,
            workflow_status_id: newStatusId,
            workflow_status: newStatus?.status_name || null,
            // Matches what the database trigger stamps, so "days in Final
            // Invoice" reads "today" straight after the move.
            workflow_status_changed_at: new Date().toISOString(),
          }
        : item
    ));

    try {
      const { data, error: rpcError } = await supabase
        .rpc('update_workflow_status', {
          p_item_id: itemId,
          p_entity_type: entityType,
          p_new_status_id: newStatusId
        });

      if (rpcError) throw rpcError;

      if (data && !data.success) {
        throw new Error(data.error || 'Failed to update status');
      }
    } catch (err) {
      console.error('Error updating workflow status:', err);
      // The card goes back where it was; say so, or it just jumps back.
      notifyReadOnly(err);
      notifySaveFailure(
        err,
        // The database words its refusals as sentences; drop the full stop
        // so this one does not read "Complete.. It has been put back."
        `Couldn't move the job to ${newStatus?.status_name ?? 'that column'}: ${errorMessage(err, 'the change was not saved').replace(/\.$/, '')}. It has been put back.`,
      );
      refetchItems();
    }
  }, [supabase, statuses, refetchItems, canUseManagerOnly, workflowType, notify]);

  // =======================================
  // HANDLER FUNCTIONS
  // =======================================

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFilter('search', e.target.value);
  }, [setFilter]);

  const handleClientChange = useCallback((clientId: string | number | null) => {
    if (clientId === null || clientId === 'none') {
      setFilter('client', '');
    } else {
      setFilter('client', clientId.toString());
    }
  }, [setFilter]);

  const handleTeamMemberChange = useCallback((value: string[]) => {
    // If "All Team Members" is selected, clear the filter
    if (Array.isArray(value) && value.includes('all')) {
      setSelectedTeamMember([]);
      setUrlFilters({ teamMember: [] });
    } else {
      setSelectedTeamMember(value);
      setUrlFilters({ teamMember: value });
    }
  }, [setUrlFilters]);

  const handleRegionChange = useCallback((value: string[]) => {
    if (Array.isArray(value) && value.includes('all')) {
      setFilter('region', []);
    } else {
      setFilter('region', value);
    }
  }, [setFilter]);

  const handleClearSearch = useCallback(() => {
    setFilter('search', '');
  }, [setFilter]);

  // The team-member filter defaults to the current user, so it is not
  // counted here: Clear takes the URL filters back to their defaults and
  // the board re-initialises the member filter the way it does on load.
  const hasActiveFilters =
    searchInput.length > 0 || selectedClient.length > 0 || selectedRegion.length > 0;
  const handleClearFilters = useCallback(() => {
    setUrlFilters({ search: '', client: '', region: [] });
  }, [setUrlFilters]);

  // =======================================
  // EFFECTS
  // =======================================

  // Fetch current user's team_member_id on mount and initialize team member filter
  useEffect(() => {
    const initUserTeamMemberId = async () => {
      if (!session) return;
      
      const userTeamMemberId = await fetchCurrentUserTeamMemberId();
      setCurrentUserTeamMemberId(userTeamMemberId);
      
      // Set default filter: use URL value if present, otherwise default to current user
      if (!defaultTeamMemberInitialized) {
        if (urlFilters.teamMember.length > 0) {
          // URL has team member filter - use it
          setSelectedTeamMember(urlFilters.teamMember);
        } else if (userTeamMemberId) {
          // No URL filter but user has team member ID - default to current user
          setSelectedTeamMember([userTeamMemberId]);
          // Don't update URL for default - keeps URL clean
        } else {
          // No team member ID, show all
          setSelectedTeamMember([]);
        }
        setDefaultTeamMemberInitialized(true);
      }
    };
    
    initUserTeamMemberId();
  }, [session, fetchCurrentUserTeamMemberId, defaultTeamMemberInitialized, urlFilters.teamMember]);

  // Fetch ALL data in parallel on mount and when workflow type changes
  useEffect(() => {
    const loadAllData = async () => {
      if (!session) return;
      // Wait until the default team member filter is initialized
      if (selectedTeamMember === null) return;

      setInitialLoading(true);
      setInitialLoadComplete(false);
      setError(null);

      try {
        // Fetch statuses, team members, assignable team members, AND items all in parallel
        const [statusesData, teamMembersData, assignableTeamMembersData, itemsData] = await Promise.all([
          fetchStatuses(),
          fetchTeamMembers(),
          fetchAssignableTeamMembers(),
          fetchItems(debouncedSearch, [], selectedClient, selectedTeamMember, selectedRegion)
        ]);

        setStatuses(statusesData);
        setTeamMembers(teamMembersData);
        setAssignableTeamMembers(assignableTeamMembersData);
        setItems(itemsData);
        setInitialLoadComplete(true);
      } catch (err) {
        console.error('Error loading workflow data:', err);
        setError('Failed to load workflow data');
      } finally {
        setInitialLoading(false);
      }
    };

    loadAllData();
    // Re-run when workflow type changes or when team member filter is first initialized
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, workflowType, selectedTeamMember === null ? null : 'initialized']);

  // Refetch items when filters change (after initial load)
  useEffect(() => {
    if (initialLoadComplete && selectedTeamMember !== null) {
      refetchItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, selectedClient, JSON.stringify(selectedTeamMember), JSON.stringify(selectedRegion)]);

  // Clear region filter when switching away from RWC workflow
  useEffect(() => {
    if (workflowType !== 'RWC') {
      setFilter('region', []);
    }
  }, [workflowType, setFilter]);

  // =======================================
  // EVENT HANDLERS
  // =======================================

  const handleCardClick = useCallback((item: WorkflowItem) => {
    // For stages, open the parent project's modal
    const projectId = item.entity_type === 'stage' ? item.parent_project_id : item.id;
    if (projectId) {
      setSelectedProjectId(projectId);
      
      // If it's a stage, set stage info for the modal title and stage ID for team members
      if (item.entity_type === 'stage' && item.parent_project_id) {
        setSelectedStageId(item.id);
        // The stage item carries its parent's description directly (the parent
        // project is not always present in `items` — e.g. RWD projects only show
        // their stages on the board), so use it rather than looking it up.
        setSelectedStageInfo({
          jobNumber: item.job_number,
          stageDescription: item.description,
          parentDescription: item.parent_description || ''
        });
      } else {
        setSelectedStageId(null);
        setSelectedStageInfo(null);
      }
      
      setDetailTab(undefined);
      setIsDetailModalOpen(true);
    }
  }, []);

  // Returning from a document opened out of this modal. The stage title text
  // comes back with the instruction: it is derived from the card that was
  // clicked, and the board may not even hold that card any more.
  useProjectDetailReopen(reopen => {
    setSelectedProjectId(reopen.projectId);
    setSelectedStageId(reopen.stageId ?? null);
    setSelectedStageInfo(reopen.stageInfo ?? null);
    setDetailTab(reopen.tab);
    setIsDetailModalOpen(true);
  });

  const handleModalClose = useCallback(() => {
    setIsDetailModalOpen(false);
    setSelectedProjectId(null);
    setSelectedStageId(null);
    setSelectedStageInfo(null);
    setDetailTab(undefined);
  }, []);

  const handleProjectUpdated = useCallback(() => {
    refetchItems();
  }, [refetchItems]);

  const handleJobCreated = useCallback(() => {
    refetchItems();
  }, [refetchItems]);

  // =======================================
  // DROPDOWN ITEMS
  // =======================================

  const teamMemberDropdownItems = useMemo<DropdownItem[]>(() => {
    const memberItems = assignableTeamMembers.map(member => ({
      id: member.id,
      label: member.full_name,
      value: member.id
    }));
    
    // Add "All Team Members" option at the top
    return [
      { id: 'all', label: 'All Team Members', value: 'all' },
      ...memberItems
    ];
  }, [assignableTeamMembers]);

  // =======================================
  // COLUMN RAIL
  // =======================================

  const { collapsedIds, setCollapsed, toggleCollapsed } = useCollapsedColumns(workflowType, statuses);
  const boardReady = !initialLoading && statuses.length > 0;
  const { activeId: activeColumnId, jumpTo } = useColumnScrollSpy(boardRef, statuses, boardReady);

  // Grouped once: the lanes render these lists and the rail counts them.
  const itemsByStatus = useMemo(
    () => groupItemsByStatus(boardItems, statuses),
    [boardItems, statuses],
  );

  const handleRailChange = useCallback((value: string) => {
    const statusId = Number(value);
    if (collapsedIds.has(statusId)) setCollapsed(statusId, false);
    jumpTo(statusId);
  }, [collapsedIds, setCollapsed, jumpTo]);

  // Keep the selected chip in view when the rail itself scrolls (tablet).
  const railRef = useRef<HTMLDivElement>(null);

  // Fade whichever edge of the rail has more chips beyond it.
  const [railOverflow, setRailOverflow] = useState({ left: false, right: false });
  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return;
    const update = () => {
      const left = rail.scrollLeft > 1;
      const right = rail.scrollLeft + rail.clientWidth < rail.scrollWidth - 1;
      setRailOverflow(prev => (prev.left === left && prev.right === right ? prev : { left, right }));
    };
    update();
    rail.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(rail);
    return () => {
      rail.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, [boardReady, statuses]);
  useEffect(() => {
    const rail = railRef.current;
    const chip = rail?.querySelector<HTMLElement>('.segmented__btn.is-active');
    if (!rail || !chip || rail.scrollWidth <= rail.clientWidth) return;
    const left = chip.offsetLeft - rail.offsetLeft;
    if (left < rail.scrollLeft || left + chip.offsetWidth > rail.scrollLeft + rail.clientWidth) {
      rail.scrollTo({ left: left - 24, behavior: 'smooth' });
    }
  }, [activeColumnId]);

  const isStatusLocked = useCallback(
    (status: WorkflowStatus) => !canUseManagerOnly && isManagerOnlyWorkflowStatus(workflowType, status.status_name),
    [canUseManagerOnly, workflowType],
  );

  const handleMove = useCallback((item: WorkflowItem, statusId: number) => {
    setMoveTarget(null);
    handleStatusChange(item.id, item.entity_type, statusId);
  }, [handleStatusChange]);

  // =======================================
  // RENDER
  // =======================================

  return (
    <ListPage
      title={WORKFLOW_TITLES[workflowType]}
      className="workflows"
      search={
        <SearchInput
          placeholder="Search by description, job number or keywords…"
          value={searchInput}
          onChange={handleSearchChange}
          onClear={handleClearSearch}
          aria-label="Search the board"
        />
      }
      actions={
        <>
          {(workflowType === 'Design' || workflowType === 'RWD') && (
            <button
              type="button"
              className="pill-btn pill-btn--sm"
              onClick={() => setIsAddStageModalOpen(true)}
              disabled={isReadOnly}
            >
              <PlusIcon /> Add stage
            </button>
          )}
          <button
            type="button"
            className="pill-btn pill-btn--solid pill-btn--sm"
            onClick={() => setIsAddJobModalOpen(true)}
            disabled={isReadOnly}
          >
            <PlusIcon /> Add {workflowType} job
          </button>
        </>
      }
      filters={
        <>
          <FilterTrigger label="Team" active={(selectedTeamMember?.length ?? 0) > 0}>
            <Dropdown
              items={teamMemberDropdownItems}
              value={selectedTeamMember || []}
              onChange={handleTeamMemberChange}
              placeholder="Everyone"
              multiSelect
              searchable
            />
          </FilterTrigger>
          <FilterTrigger label="Client" active={selectedClient.length > 0}>
            <SearchableClientDropdown
              value={selectedClient.length > 0 ? selectedClient[0] : null}
              onChange={handleClientChange}
              placeholder="All"
              allowNone={true}
              noneLabel="All Clients"
            />
          </FilterTrigger>
          {workflowType === 'RWC' && (
            <FilterTrigger label="Region" active={selectedRegion.length > 0}>
              <Dropdown
                items={[
                  { id: 'all', label: 'All Regions', value: 'all' },
                  ...RWC_REGION_OPTIONS.map(region => ({ id: region, label: region, value: region })),
                ]}
                value={selectedRegion}
                onChange={handleRegionChange}
                placeholder="All"
                multiSelect
                searchable={false}
              />
            </FilterTrigger>
          )}
        </>
      }
      segments={boardReady ? (
        <div
          ref={railRef}
          className={[
            'column-rail',
            railOverflow.left ? 'has-more-left' : '',
            railOverflow.right ? 'has-more-right' : '',
          ].filter(Boolean).join(' ')}
        >
          <StatusSegments
            aria-label="Jump to column"
            size="sm"
            value={String(activeColumnId ?? statuses[0].id)}
            onChange={handleRailChange}
            segments={statuses.map(status => ({
              value: String(status.id),
              label: shortStatusLabel(status.status_name),
              count: itemsByStatus.get(status.id)?.length ?? 0,
            }))}
          />
        </div>
      ) : undefined}
      hasActiveFilters={hasActiveFilters}
      onClearFilters={handleClearFilters}
    >
      {error && (
        <div className="list-page__error">
          {error}{' '}
          <button type="button" className="link-btn" onClick={() => window.location.reload()}>Try again</button>
        </div>
      )}

      {/* Kanban Board */}
      {initialLoading ? (
        <div className="kanban-board kanban-board--skeleton" aria-busy="true" aria-label="Loading board">
          {[3, 2, 4, 1, 2].map((cards, i) => (
            <div key={i} className="kanban-column">
              <div className="kanban-column__header">
                <span className="skeleton-block skeleton-block--pill" />
              </div>
              <div className="kanban-column__content">
                {Array.from({ length: cards }, (_, j) => (
                  <div key={j} className="skeleton-block skeleton-block--card" />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : statuses.length === 0 ? (
        <div className="kanban-empty">
          <h3>No workflow statuses configured</h3>
          <p>Please run the database seed script to set up workflow statuses.</p>
        </div>
      ) : (
        <KanbanBoard
          statuses={statuses}
          itemsByStatus={itemsByStatus}
          collapsedIds={collapsedIds}
          onToggleCollapsed={toggleCollapsed}
          onStatusChange={handleStatusChange}
          onCardClick={handleCardClick}
          onMoveRequest={isReadOnly ? undefined : setMoveTarget}
          boardRef={boardRef}
          isLoading={itemsLoading}
        />
      )}

      <MoveToModal
        item={moveTarget}
        statuses={statuses}
        isLocked={isStatusLocked}
        onClose={() => setMoveTarget(null)}
        onMove={handleMove}
      />

      {/* Project Detail Modal */}
      <ProjectDetailModal
        isOpen={isDetailModalOpen}
        projectId={selectedProjectId}
        stageId={selectedStageId}
        stageInfo={selectedStageInfo}
        initialTab={detailTab}
        onClose={handleModalClose}
        onProjectUpdated={handleProjectUpdated}
        onProjectDeleted={handleProjectUpdated}
        teamMembers={teamMembers}
      />

      {/* Add Job Modal */}
      <AddJobModal
        isOpen={isAddJobModalOpen}
        onClose={() => setIsAddJobModalOpen(false)}
        onJobCreated={handleJobCreated}
        teamMembers={assignableTeamMembers}
        defaultType={workflowType}
      />

      {/* Add Stage Modal - only for Design and RWD */}
      {(workflowType === 'Design' || workflowType === 'RWD') && (
        <AddStageModal
          isOpen={isAddStageModalOpen}
          onClose={() => setIsAddStageModalOpen(false)}
          onStageCreated={handleJobCreated}
          teamMembers={assignableTeamMembers}
          workflowType={workflowType}
        />
      )}
      {noticeDialog}
    </ListPage>
  );
};

export default Workflows;
